import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import {
  Form,
  Link,
  useActionData,
  useNavigation,
} from "@remix-run/react";
import { parse } from "valibot";
import { LoginSchema } from "~/types/schema";
import {
  NOT_ALOOW_INPUT,
  signUp,
} from "~/utils/authUtils";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import { parseWithValibot as conformParse } from "conform-to-valibot";
import {
  getFormProps,
  getInputProps,
  useForm,
} from "@conform-to/react";
import ErrorDisplay from "~/components/Common/ErrorDisplay";
import clsx from "clsx";
import {
  buttonBase,
  inputBase,
} from "~/components/Common/FormStyle";
import { UserService } from "~/database/user.service";
import {
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";
import { UserService2 } from "~/database/user2.service";

export const meta: MetaFunction = () => {
  return [
    { title: "ログイン" + TITLE_SUFFIX },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
  ];
};

// add the loader
export async function loader({
  request,
  context,
}: LoaderFunctionArgs) {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const userResponse =
    await supabase.auth.getSession();
  const userData = await UserService2.getUser(
    supabase,
    userResponse.data.session?.user.id as string,
  );
  if (
    !userData.data &&
    userResponse.data.session?.user.id
  ) {
    await supabase.auth.signOut();
    return null;
  }
  if (userResponse.data.session?.user.id) {
    return redirect(
      `/user/${userResponse.data.session?.user.id}`,
      {
        headers: headers,
      },
    );
  } else {
    return null;
  }
}

export async function action({
  request,
  context,
}: ActionFunctionArgs) {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const formData = await request.formData();
  const dataFields = Object.fromEntries(
    formData.entries(),
  );
  const submission = conformParse(formData, {
    schema: LoginSchema,
  });

  if (submission.status !== "success") {
    return json({
      success: false,
      error: {
        auth: NOT_ALOOW_INPUT,
        ...submission.error,
      },
      submission,
    });
  }

  const parsed = parse(LoginSchema, dataFields);
  const { error } =
    await supabase.auth.signInWithPassword({
      email: parsed.email,
      password: parsed.password,
    });
  if (error) {
    if (error.message === "Email not confirmed") {
      const url = new URL(request.url);
      const siteURLRoot = url.origin;
      const { error } = await signUp(
        supabase,
        {
          email: parsed.email,
          password: parsed.password,
        },
        siteURLRoot,
      );
      if (error) {
        return json({
          error: {
            auth: error.message,
          },
        });
      }
      return redirect("/sent-mail", {
        headers: headers,
      });
    }
    return json({
      error: {
        auth: error.message,
      },
    });
  } else {
    const exist =
      await UserService.isExistMineUser(supabase);
    if (!exist) {
      return redirect("/init", {
        headers: headers,
      });
    } else {
      return redirect("/dashboard", {
        headers: headers,
      });
    }
  }
}

export default function Index() {
  const data = useActionData<typeof action>();
  const [form, { email, password }] = useForm({
    onValidate({ formData }) {
      const check = conformParse(formData, {
        schema: LoginSchema,
      });
      return check;
    },
  });
  const { state } = useNavigation();
  return (
    <div className={clsx("pt-96")}>
      <h1
        className={clsx(
          "font-bold",
          "text-lg",
          "text-center",
        )}
      >
        LOGIN
      </h1>
      <div
        className={clsx(
          "mx-auto max-w-contentMd",
          "mt-48",
        )}
      >
        <Form
          method="post"
          {...getFormProps(form)}
        >
          <div
            className={clsx(
              "flex",
              "flex-col",
              "gap-16",
            )}
          >
            <div className="">
              <input
                className={inputBase()}
                placeholder="username"
                {...getInputProps(email, {
                  type: "email",
                })}
              />
              {email.errors && (
                <ErrorDisplay
                  error={{
                    email: email.errors,
                  }}
                ></ErrorDisplay>
              )}
            </div>
            <div className="">
              <input
                className={inputBase()}
                placeholder="password"
                {...getInputProps(password, {
                  type: "password",
                })}
              />
              {password.errors && (
                <ErrorDisplay
                  error={{
                    password: password.errors,
                  }}
                ></ErrorDisplay>
              )}
            </div>
          </div>

          <div
            className={clsx(
              "underline",
              "mt-12",
              "text-xs",
              "text-center",
              "text-gray-500",
            )}
          >
            <Link to="/password-reset">
              パスワードを忘れた
            </Link>
          </div>

          <div
            className={clsx(
              "text-center",
              "mt-24",
            )}
          >
            <button
              className={buttonBase({
                loading:
                  state === "loading" ||
                  state === "submitting",
              })}
              type="submit"
            >
              LOGIN
            </button>
          </div>
        </Form>
        <div
          className={clsx(
            "text-center",
            "text-red-500",
            "pb-10",
          )}
        >
          <ErrorDisplay
            error={data?.error}
          ></ErrorDisplay>
        </div>
        <div
          className={clsx(
            "underline",
            "mt-32",
            "text-sm",
            "text-center",
          )}
        >
          <Link to="/create-account">
            CREATE ACCOUNT
          </Link>
        </div>
      </div>
    </div>
  );
}
